import { useMediaQuery, useTheme } from '@mui/material';
import type {
  GridColumnVisibilityModel,
  GridRowParams,
  GridRowSelectionModel,
} from '@mui/x-data-grid-pro';
import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ScrollableDataGrid } from '~/components/data-grid';
import { EmptyState } from '~/components/empty-state';
import { ListCheckbox } from '~/components/list-row';
import { useAppContext } from '~/contexts';
import CreateChannelGuideIllustration from '~/images/illustrations/create-channel-guide.svg';
import { AddChannelToGuideDialog } from '../../components/AddChannelToGuideDialog';
import type { ChannelList__Channel as Channel } from '../../queries/list.generated';
import { useColumns } from '../lib/columns';

const NoRowsOverlay = () => {
  const { t } = useTranslation();
  return (
    <EmptyState
      illustration={CreateChannelGuideIllustration}
      header={t('pages.channels.empty_state.header')}
      description={t('pages.channels.empty_state.description')}
    />
  );
};

export interface TableProps {
  channels: readonly Channel[];
  loading: boolean;
  onCheck: Dispatch<SetStateAction<GridRowSelectionModel>>;
  selectedIds: GridRowSelectionModel;
}

export const Table = ({ channels, loading, onCheck, selectedIds }: TableProps) => {
  const { currentNetwork } = useAppContext();
  const [selectedChannel, setSelectedChannel] = useState<Channel | null>(null);
  const [addChannelDialogOpen, setAddChannelDialogOpen] = useState(false);

  const handleAddToClick = useCallback((channel: Channel) => {
    setSelectedChannel(channel);
    setAddChannelDialogOpen(true);
  }, []);

  const columns = useColumns(handleAddToClick, currentNetwork.settings.enableLiveChannelStreaming);
  const [showColumns, setShowColumns] = useState<GridColumnVisibilityModel>({});
  const navigate = useNavigate();

  const theme = useTheme();
  const isSmallAndDown = useMediaQuery(theme.breakpoints.down('sm'));
  const isLargeAndDown = useMediaQuery(theme.breakpoints.down('lg'));
  const isXLargeAndDown = useMediaQuery(theme.breakpoints.down('xl'));

  useEffect(() => {
    setShowColumns((x) => ({
      ...x,
      channelGuides: !isXLargeAndDown,
      enabledForLiveStreaming: !isXLargeAndDown,
      encoding: !isXLargeAndDown,
      encryption: !isXLargeAndDown,
      outputUrl: !isLargeAndDown,
      source: !isXLargeAndDown,
      thumbnailFile: !isXLargeAndDown,
      url: !isLargeAndDown,
    }));
  }, [isLargeAndDown, isSmallAndDown, isXLargeAndDown]);

  return (
    <>
      <ScrollableDataGrid
        checkboxSelection={!isSmallAndDown}
        columns={columns}
        columnVisibilityModel={showColumns}
        disableRowSelectionOnClick
        loading={loading}
        onColumnVisibilityModelChange={setShowColumns}
        onRowSelectionModelChange={onCheck}
        onRowClick={({ row }: GridRowParams<Channel>) => navigate(String(row.id))}
        rows={channels}
        rowSelectionModel={selectedIds}
        slots={{
          baseCheckbox: ListCheckbox,
          noRowsOverlay: NoRowsOverlay,
        }}
      />
      {selectedChannel && (
        <AddChannelToGuideDialog
          channelId={selectedChannel.id}
          close={() => {
            setSelectedChannel(null);
            setAddChannelDialogOpen(false);
          }}
          open={addChannelDialogOpen}
        />
      )}
    </>
  );
};
